import * as React from 'react';
import { Typography } from '@SBGSports/referee-react';
import { useTranslations } from 'i18n';
import { RadioButton } from '../../reporting/components/radio-button/radio-button';
import styles from '../focus-import.module.css';
import { AthleteParticipationByTagType } from './athlete-participation-by-tag-type';
import { AthleteParticipationByFocusLabels } from './athlete-participation-by-focus-labels';
import { useFocusImport } from '../use-focus-import';
import { isEmpty } from 'lodash';
import { LABELS_WITHIN_TAG_OPTION, TAG_TYPE_OPTION } from '../constants';
import { useFocusStoredOptions } from '../use-focus-stored-options';
import { getUniqueFocusLabels, getUniqueFocusTags } from 'focus/focus-utils';
import { useEffect, useMemo, useRef } from 'react';
import mixpanel from 'mixpanel-browser';
import { EVENTS } from 'analytics/analytics-events';
import { useFocusImportSelections } from '../use-focus-import-selections';

interface AthleteParticipationPIPSelectorProps {
    filterBySelectedTags?: boolean;
    onValueChanged: (athleteAssignment: Map<string, string>) => void;
}

export const AthleteParticipationPIPSelector: React.FC<AthleteParticipationPIPSelectorProps> = (props) => {
    const { onValueChanged, filterBySelectedTags = true } = props;
    //Hooks
    const { __ } = useTranslations();
    const { focusStoredOptions, setFocusStoredOptions } = useFocusStoredOptions();
    const { selectionsRef, setAthleteAssignmentOption } = useFocusImportSelections();
    const { focusFile } = useFocusImport();

    /**
     *  set initial selection if historical exists
     */
    const initialRef = useRef(true);
    useEffect(() => {
        if (initialRef.current && focusStoredOptions.athleteParticipationType) {
            setAthleteAssignmentOption(focusStoredOptions.athleteParticipationType);
            initialRef.current = false;
        }
    }, [focusStoredOptions.athleteParticipationType, setAthleteAssignmentOption]);

    //Memos
    const uniqueFocusLabels = useMemo(() => {
        return getUniqueFocusLabels(
            focusFile?.tags
                ?.filter((tag) => (filterBySelectedTags ? selectionsRef.current.tagAlias.has(tag.tagName) : !!tag))
                ?.flatMap((tag) => tag.tagLabels) ?? [],
        );
    }, [filterBySelectedTags, focusFile?.tags, selectionsRef]);

    const uniqueFocusTags: string[] = useMemo(() => {
        return getUniqueFocusTags(
            focusFile?.tags?.filter((tag) =>
                filterBySelectedTags ? !selectionsRef.current.tagAlias.has(tag.tagName) : !!tag,
            ) ?? [],
        );
    }, [filterBySelectedTags, focusFile?.tags, selectionsRef]);

    const isLabelEmpty = isEmpty(uniqueFocusLabels);

    return (
        <div data-testid="athlete-participation-pip-selector" className={styles['page-body']}>
            <div>
                <Typography variant="body-1">
                    {__('smt_importer.select_how_you_would_like_to_assign_athletes_to_openfield')}
                </Typography>
            </div>
            <div className={styles['options']}>
                <RadioButton
                    label={__('misc.tag_types')}
                    onChange={() => {
                        setFocusStoredOptions({
                            ...focusStoredOptions,
                            athleteParticipationType: TAG_TYPE_OPTION,
                        });
                        mixpanel.track(EVENTS.focus.athleteParticipationTypeSelected, {
                            selectedOption: TAG_TYPE_OPTION,
                        });
                        setAthleteAssignmentOption(TAG_TYPE_OPTION);
                    }}
                    value={TAG_TYPE_OPTION}
                    isChecked={focusStoredOptions.athleteParticipationType === TAG_TYPE_OPTION}
                    name={'athlete_participation_pip_option'}
                    inline
                />
                <RadioButton
                    label={__('smt_importer.labels_within_tags')}
                    onChange={() => {
                        setFocusStoredOptions({
                            ...focusStoredOptions,
                            athleteParticipationType: LABELS_WITHIN_TAG_OPTION,
                        });
                        mixpanel.track(EVENTS.focus.athleteParticipationTypeSelected, {
                            selectedOption: LABELS_WITHIN_TAG_OPTION,
                        });
                        setAthleteAssignmentOption(LABELS_WITHIN_TAG_OPTION);
                    }}
                    value={LABELS_WITHIN_TAG_OPTION}
                    isChecked={focusStoredOptions.athleteParticipationType === LABELS_WITHIN_TAG_OPTION}
                    name={'athlete_participation_pip_option'}
                    disabled={isLabelEmpty}
                    description={isLabelEmpty ? __('smt_importer.disabled_option_because_of_no_labels') : undefined}
                />
            </div>
            <div>
                {focusStoredOptions.athleteParticipationType === TAG_TYPE_OPTION && (
                    <AthleteParticipationByTagType uniqueFocusTags={uniqueFocusTags} onValueChanged={onValueChanged} />
                )}
            </div>
            <div>
                {focusStoredOptions.athleteParticipationType === LABELS_WITHIN_TAG_OPTION && (
                    <AthleteParticipationByFocusLabels
                        onValueChanged={onValueChanged}
                        uniqueFocusLabels={uniqueFocusLabels}
                    />
                )}
            </div>
        </div>
    );
};
